import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useWindowWidth } from '../hooks/useWindowWidth';
import { Logo } from './Logo';

const HEIGHT = 60;

const INK   = '#0A1F4A';
const INK2  = '#3D4F78';
const INK3  = '#7883A2';
const BG    = '#ECEEF3';
const RULE  = '#D6DAE4';
const ACC   = '#FF7A3D';

const s = {
  nav: {
    position: 'sticky',
    top: 0,
    zIndex: 300,
    height: HEIGHT,
    background: 'rgba(236,238,243,0.88)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    borderBottom: `1px solid ${RULE}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
  },
  left: { display: 'flex', alignItems: 'center', gap: 32 },
  logoWrap: {
    display: 'flex', alignItems: 'center', gap: 10,
    fontWeight: 700, fontSize: 18, color: INK,
    letterSpacing: '-0.02em', textDecoration: 'none',
  },
  logoMark: {
    width: 26, height: 26,
    background: INK,
    borderRadius: 6,
    position: 'relative',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  links: { display: 'flex', gap: 2, alignItems: 'center' },
  link: (active) => ({
    fontSize: 14,
    fontWeight: 500,
    color: active ? BG : INK2,
    background: active ? INK : 'transparent',
    padding: '7px 13px',
    borderRadius: 8,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    transition: 'background 160ms, color 160ms',
  }),
  right: { display: 'flex', gap: 8, alignItems: 'center' },
  email: {
    fontSize: 13,
    color: INK3,
    fontFamily: '"JetBrains Mono", monospace',
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  ghostBtn: {
    fontSize: 14, fontWeight: 500,
    color: INK, background: 'transparent',
    border: `1px solid ${INK}`,
    padding: '7px 14px', borderRadius: 8,
    textDecoration: 'none', cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background 160ms, color 160ms',
    display: 'inline-block',
  },
  primaryBtn: {
    fontSize: 14, fontWeight: 600,
    color: BG, background: INK,
    padding: '7px 16px', borderRadius: 8,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    boxShadow: `0 4px 0 0 ${ACC}`,
    display: 'inline-block',
    transition: 'box-shadow 160ms, transform 160ms',
  },
  hamburger: {
    background: 'none', border: 'none',
    color: INK, fontSize: 22,
    cursor: 'pointer', padding: '4px 8px', lineHeight: 1,
  },
  mobileMenu: {
    position: 'fixed',
    top: HEIGHT, left: 0, right: 0,
    background: '#fff',
    borderBottom: `1px solid ${RULE}`,
    padding: '12px 24px 20px',
    display: 'flex', flexDirection: 'column', gap: 4,
    zIndex: 299,
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
  },
  mobileLink: (active) => ({
    fontSize: 15, fontWeight: active ? 600 : 400,
    color: active ? INK : INK2,
    padding: '10px 0',
    textDecoration: 'none',
    borderBottom: `1px solid ${RULE}`,
    background: 'transparent',
    display: 'block',
  }),
  mobileDivider: { borderTop: `1px solid ${RULE}`, margin: '8px 0' },
  mobileCta: {
    marginTop: 4,
    display: 'block', textAlign: 'center',
    background: INK, color: BG,
    fontWeight: 600, fontSize: 15,
    padding: '12px', borderRadius: 8,
    textDecoration: 'none',
    boxShadow: `0 4px 0 0 ${ACC}`,
  },
  mobileGhost: {
    marginTop: 8,
    display: 'block', textAlign: 'center',
    background: 'transparent',
    border: `1px solid ${RULE}`,
    color: INK, fontWeight: 500, fontSize: 15,
    padding: '11px', borderRadius: 8,
    cursor: 'pointer',
  },
};

const NAV_LINKS = [
  { to: '/how-it-works', label: 'How it works' },
  { to: '/pricing',      label: 'Pricing' },
  { to: '/guide',        label: 'Tender Guide', match: (p) => p.startsWith('/guide') },
  { to: '/blog',         label: 'Blog',         match: (p) => p.startsWith('/blog') },
];

export default function TopNav({ isAuth, isAdmin, email, onLogout }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const width = useWindowWidth();
  const isMobile = width < 768;
  const [menuOpen, setMenuOpen] = useState(false);

  function handleLogout() {
    setMenuOpen(false);
    onLogout();
    navigate('/login');
  }

  function isActive(link) {
    return link.match ? link.match(pathname) : pathname === link.to;
  }

  return (
    <>
      <nav style={s.nav}>
        <div style={s.left}>
          <Link to="/" style={s.logoWrap}>
            <Logo tone="color" />
          </Link>
          {!isMobile && (
            <div style={s.links}>
              {NAV_LINKS.map((link) => (
                <Link key={link.to} to={link.to} style={s.link(isActive(link))}>
                  {link.label}
                </Link>
              ))}
            </div>
          )}
        </div>

        {isMobile ? (
          <button style={s.hamburger} onClick={() => setMenuOpen((o) => !o)} aria-label="Menu">
            {menuOpen ? '✕' : '☰'}
          </button>
        ) : (
          <div style={s.right}>
            {isAuth ? (
              <>
                {email && <span style={s.email}>{email}</span>}
                {isAdmin && (
                  <Link to="/admin" style={{
                    ...s.ghostBtn, fontSize: 13, padding: '5px 12px',
                    color: '#60a5fa', borderColor: '#1e3a5f',
                    background: '#0A1F4A', fontWeight: 600,
                  }}>
                    Admin
                  </Link>
                )}
                <Link to="/dashboard" style={s.primaryBtn}>Dashboard →</Link>
                <button style={{ ...s.ghostBtn, cursor: 'pointer' }} onClick={handleLogout}>Log out</button>
              </>
            ) : (
              <>
                <Link to="/login"  style={s.ghostBtn}>Log in</Link>
                <Link to="/signup" style={s.primaryBtn}>Start free trial →</Link>
              </>
            )}
          </div>
        )}
      </nav>

      {isMobile && menuOpen && (
        <div style={s.mobileMenu}>
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              style={s.mobileLink(isActive(link))}
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          <div style={s.mobileDivider} />
          {isAuth ? (
            <>
              {email && <div style={{ fontSize: 13, color: INK3, fontFamily: '"JetBrains Mono", monospace', paddingBottom: 4 }}>{email}</div>}
              <Link to="/dashboard" style={s.mobileCta} onClick={() => setMenuOpen(false)}>Dashboard →</Link>
              <button style={s.mobileGhost} onClick={handleLogout}>Log out</button>
            </>
          ) : (
            <>
              <Link to="/login"  style={{ ...s.mobileLink(false), borderBottom: 'none' }} onClick={() => setMenuOpen(false)}>Log in</Link>
              <Link to="/signup" style={s.mobileCta} onClick={() => setMenuOpen(false)}>Start free trial →</Link>
            </>
          )}
        </div>
      )}
    </>
  );
}
